import axios from 'axios';
import DatabaseController from './DatabaseController';
import LoginController from './LoginController';


/**
 * Controller para padronizar as chamadas aos métodos da API
 */
class ApiController {

    constructor() {
        this.databaseController = new DatabaseController();
    }

    /**
     * Chama a API da aplicação, com a URL e os parâmetros informados
     * @param {String} url A url do serviço a ser chamado
     * @param {*} params Query params a serem enviados para o serviço
     * @param {Boolean} requerLogin Indica se o usuário precisa estar logado para usar esse método (Padrão: true)
     * @param {String} responseType Indica o tipo de resposta da requisição, padrão json
     * @return {Promise<*>} Retorna um promise com a resposta do axios
     */
    async get(url, params, requerLogin = true, responseType = 'json') {
        //A URL é obrigatória
        if (!url)
            throw new TypeError('URL é necessária');

        //Carrega os headers que devem ser enviados na requisição
        const headers = await this._obterHeaders(requerLogin);

        try {
            //Executa a chamada ao método da API
            return await axios.get(this.adicionarPrefixo(url), { params, headers, responseType });
        } catch (error) {
            //Caso retorne um erro de login inválido
            if (error && error.response && error.response.status === 401)
                LoginController.fireSessionExpired();
            throw error;
        }
    }

    /**
     * Chama a API da aplicação com a URL e os parâmetros informados
     * @param {String} url A url do serviço a ser chamado
     * @param {*} body Corpo da requisição a ser enviado
     * @param {Boolean} requerLogin Indica se o usuário precisa estar logado para usar esse método (Padrão: true)
     * @return {Promise<*>} Retorna um promise com a resposta do axios
     */
    async post(url, body, requerLogin = true) {
        //A URL é obrigatória
        if (!url)
            throw new TypeError('URL é necessária');
        if (!body)
            throw new TypeError('Corpo é necessário');

        //Carrega os headers que devem ser enviados na requisição
        const headers = await this._obterHeaders(requerLogin);

        try {
            //Executa a chamada ao método da API
            return await axios.post(this.adicionarPrefixo(url), body, { headers });
        } catch (error) {
            //Caso retorne um erro de login inválido
            if (error && error.response && error.response.status === 401)
                LoginController.fireSessionExpired();
            throw error;
        }
    }

    /**
     * Chama a API da aplicação com a URL e os parâmetros informados
     * @param {String} url A url do serviço a ser chamado
     * @param {*} body Corpo da requisição a ser enviado
     * @param {Boolean} requerLogin Indica se o usuário precisa estar logado para usar esse método (Padrão: true)
     * @return {Promise<*>} Retorna um promise com a resposta do axios
     */
    async put(url, body, requerLogin = true) {
        //A URL é obrigatória
        if (!url)
            throw new TypeError('URL é necessária');
        if (!body)
            throw new TypeError('Corpo é necessário');

        //Carrega os headers que devem ser enviados na requisição
        const headers = await this._obterHeaders(requerLogin);

        try {
            //Executa a chamada ao método da API
            return await axios.put(this.adicionarPrefixo(url), body, { headers });
        } catch (error) {
            //Caso retorne um erro de login inválido
            if (error && error.response && error.response.status === 401)
                LoginController.fireSessionExpired();
            throw error;
        }
    }

    /**
     * Chama a API da aplicação com a URL e os parâmetros informados
     * @param {String} url A url do serviço a ser chamado
     * @param {*} body Corpo da requisição a ser enviado
     * @param {Boolean} requerLogin Indica se o usuário precisa estar logado para usar esse método (Padrão: true)
     * @return {Promise<*>} Retorna um promise com a resposta do axios
     */
    async patch(url, body, requerLogin = true) {
        //A URL é obrigatória
        if (!url)
            throw new TypeError('URL é necessária');
        if (!body)
            throw new TypeError('Corpo é necessário');

        //Carrega os headers que devem ser enviados na requisição
        const headers = await this._obterHeaders(requerLogin);

        try {
            //Executa a chamada ao método da API
            return await axios.patch(this.adicionarPrefixo(url), body, { headers });
        } catch (error) {
            //Caso retorne um erro de login inválido
            if (error && error.response && error.response.status === 401)
                LoginController.fireSessionExpired();
            throw error;
        }
    }

    /**
     * Adiciona um prefixo na URL enviada, de acordo com as variáveis de ambiente
     * @param {String} url A url a ser editada
     * @returns {String} Retorna a URL montada
     */
    adicionarPrefixo(url) {
        if (process.env.REACT_APP_API_URL) {
            // Remove a barra inicial da rota se existir
            const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
            // Garante que o prefixo termine com uma barra
            const prefix = process.env.REACT_APP_API_URL.endsWith('/')
                ? process.env.REACT_APP_API_URL
                : process.env.REACT_APP_API_URL + '/';

            url = prefix + cleanUrl;

            // Se o prefixo for um domínio mas não tiver protocolo, adiciona https://
            if (url.includes('.') && !url.startsWith('http')) {
                url = 'https://' + url;
            }
        }
        return url;
    }

    /**
     * Retorna os headers da requisição montados
     * @param {Boolean} requerLogin Indica se o header de autorização deve estar presente
     * @returns {Object} O objeto contendo os headers da requisição
     */
    async _obterHeaders(requerLogin) {
        let headers = {};

        if (requerLogin) {
            //Obtém o token de autenticação
            const token = await this.databaseController.carregar('config', 'token');
            headers = {
                'Authorization': 'Bearer ' + token
            };
        }

        return headers;
    }

}

export default ApiController;